import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { pollFileUploadProgress } from '@/lib/uploadProgressManager';
import { pollOutlookSync } from '@/lib/outlookSyncManager';
import calendarSync from '@/components/contexts/background-sync/calendarSync';
import { useAuth } from '@/lib/auth/hooks/auth-provider';

type SyncState = Record<string, any>;

interface SyncFunction<TState = any> {
  name: string;
  execute: (params?: any) => Promise<TState | void>;
  intervalMs: number;
  runOnMount?: boolean;
  updatesState?: boolean;
}

interface BackgroundSyncContextType {
  getState: <T = any>(syncName: string) => T | undefined;
  subscribe: (syncName: string, listener: () => void) => () => void;
  triggerSync: (syncName: string) => Promise<void>;
  registerPendingUpload: (matterGuid: string) => void;
  getPendingUploads: (matterGuid: string) => number[];
  clearPendingUpload: (matterGuid: string, timestamp: number) => void;
}

const BackgroundSyncContext = createContext<BackgroundSyncContextType | undefined>(undefined);

const BACKGROUND_SYNCS: SyncFunction[] = [
  {
    name: 'file-upload-progress',
    execute: pollFileUploadProgress,
    intervalMs: 8000, // 8 seconds
    runOnMount: true,
    updatesState: true,
  },
  {
    name: 'outlook-sync',
    execute: pollOutlookSync,
    intervalMs: 90000, // 75 seconds = 1 minute 15 seconds
    runOnMount: true,
    updatesState: true,
  },
  {
    name: 'calendar-reconciliation',
    execute: calendarSync,
    intervalMs: 60000, // 60 seconds
    runOnMount: true,
    updatesState: false,
  },
];

export const BackgroundSyncProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [syncState, setSyncState] = useState<SyncState>({});
  const [listeners] = useState<Map<string, Set<() => void>>>(() => new Map());
  const syncFunctionsRef = useRef<Map<string, SyncFunction>>(new Map());
  const [triggerCount, setTriggerCount] = useState(0);

  // Track pending uploads by matter GUID - stores timestamps of when uploads were initiated
  const [pendingUploads, setPendingUploads] = useState<Record<string, number[]>>({});

  // Listen for matter changes via custom event
  useEffect(() => {
    const handleMatterChange = () => {
      setTriggerCount(prev => prev + 1);
    };

    window.addEventListener('selectedCaseChanged', handleMatterChange);

    return () => {
      window.removeEventListener('selectedCaseChanged', handleMatterChange);
    };
  }, []);

  // Build sync function lookup
  useEffect(() => {
    BACKGROUND_SYNCS.forEach(sync => {
      syncFunctionsRef.current.set(sync.name, sync);
    });
  }, []);

  const subscribe = useCallback((syncName: string, listener: () => void) => {
    if (!listeners.has(syncName)) {
      listeners.set(syncName, new Set());
    }
    listeners.get(syncName)!.add(listener);
    return () => listeners.get(syncName)?.delete(listener);
  }, [listeners]);

  const getState = useCallback(<T = any,>(syncName: string): T | undefined => {
    return syncState[syncName] as T | undefined;
  }, [syncState]);

  const notifyListeners = useCallback((syncName: string) => {
    listeners.get(syncName)?.forEach(listener => listener());
  }, [listeners]);

  const runSync = useCallback(async (sync: SyncFunction, params?: any) => {
    try {
      const result = await sync.execute(params);
      if (sync.updatesState && result !== undefined) {
        // For file-upload-progress, don't clear existing state if result is empty
        // This prevents flickering when API fails or matterGuid is temporarily unavailable
        if (sync.name === 'file-upload-progress' && Array.isArray(result) && result.length === 0) {
          setSyncState(prev => {
            const existingState = prev[sync.name];
            if (existingState && Array.isArray(existingState) && existingState.length > 0) {
              // Keep existing state instead of clearing it
              return prev;
            }
            return {
              ...prev,
              [sync.name]: result
            };
          });
          // Notify listeners immediately for triggered syncs (not relying on useEffect)
          notifyListeners(sync.name);
          return;
        }

        setSyncState(prev => ({
          ...prev,
          [sync.name]: result
        }));
        // Notify listeners immediately for triggered syncs (not relying on useEffect)
        notifyListeners(sync.name);
      }
    } catch (error) {
      console.error(`Background sync function "${sync.name}" failed:`, error);
    }
  }, [notifyListeners]);

  const triggerSync = useCallback(async (syncName: string) => {
    const sync = syncFunctionsRef.current.get(syncName);
    if (sync) {
      // Get params for sync function (same logic as interval sync)
      let params: any = undefined;
      if (syncName === 'outlook-sync' || syncName === 'file-upload-progress') {
        if (typeof window !== 'undefined') {
          params = localStorage.getItem('selectedCase') || undefined;
        }
      }
      await runSync(sync, params);
    }
  }, [runSync]);

  // Register a pending upload for immediate feedback
  const registerPendingUpload = useCallback((matterGuid: string) => {
    const timestamp = Date.now();
    setPendingUploads(prev => ({
      ...prev,
      [matterGuid]: [...(prev[matterGuid] || []), timestamp]
    }));

    // Notify listeners so UI updates immediately
    notifyListeners('file-upload-progress');
  }, [notifyListeners]);

  // Get all pending uploads for a matter
  const getPendingUploads = useCallback((matterGuid: string): number[] => {
    return pendingUploads[matterGuid] || [];
  }, [pendingUploads]);

  // Clear a specific pending upload
  const clearPendingUpload = useCallback((matterGuid: string, timestamp: number) => {
    setPendingUploads(prev => {
      const uploads = prev[matterGuid] || [];
      const filtered = uploads.filter(t => t !== timestamp);

      if (filtered.length === 0) {
        const { [matterGuid]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [matterGuid]: filtered
      };
    });

    // Notify listeners so UI updates
    notifyListeners('file-upload-progress');
  }, [notifyListeners]);

  // Notify listeners when sync state changes
  useEffect(() => {
    // Notify all listeners for all sync functions that have state
    Object.keys(syncState).forEach(syncName => {
      notifyListeners(syncName);
    });
  }, [syncState, notifyListeners]);

  useEffect(() => {
    if (!user) return;

    console.log('🔄 Initializing background sync intervals');
    const intervals: NodeJS.Timeout[] = [];
    let isMounted = true;

    // Wrapper for runSync that checks mount status
    const runSyncIfMounted = async (sync: SyncFunction, params?: any) => {
      if (!isMounted) return;
      await runSync(sync, params);
    };

    BACKGROUND_SYNCS.forEach((sync) => {
      console.log(`⏰ Setting up "${sync.name}" sync: interval ${sync.intervalMs}ms`);

      // Get params for this sync function (read directly from localStorage)
      const getParams = () => {
        if (sync.name === 'outlook-sync' || sync.name === 'file-upload-progress') {
          if (typeof window !== 'undefined') {
            return localStorage.getItem('selectedCase') || undefined;
          }
        }
        return undefined;
      };

      if (sync.runOnMount !== false) {
        // Only run on mount if we have the required params
        const params = getParams();
        if (params || (sync.name !== 'outlook-sync' && sync.name !== 'file-upload-progress')) {
          runSyncIfMounted(sync, params);
        }
      }

      const interval = setInterval(() => {
        runSyncIfMounted(sync, getParams());
      }, sync.intervalMs);

      intervals.push(interval);
    });

    return () => {
      isMounted = false;
      intervals.forEach((interval) => clearInterval(interval));
    };
  }, [runSync, triggerCount, user]);

  const contextValue: BackgroundSyncContextType = {
    getState,
    subscribe,
    triggerSync,
    registerPendingUpload,
    getPendingUploads,
    clearPendingUpload,
  };

  return (
    <BackgroundSyncContext.Provider value={contextValue}>
      {children}
    </BackgroundSyncContext.Provider>
  );
};

export const useBackgroundSync = (): BackgroundSyncContextType => {
  const context = useContext(BackgroundSyncContext);
  if (!context) {
    throw new Error('useBackgroundSync must be used within a BackgroundSyncProvider');
  }
  return context;
};
